import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";

import PageTitle from "../../../layouts/PageTitle";
import { appApi, useGetNbrRunsQuery, useGetNbrRunQuery } from "../../../../store/appApi";
import {
  usePartnerCode,
  PartnerPicker,
  panelClass,
  fieldClass,
  labelClass,
  btnPrimary,
  btnGhost,
  Badge,
  fmtDate,
  fmtDateInput,
} from "../customerStatus/config";
import {
  createReconRun,
  downloadReconExcel,
} from "../../../../services/netplusBankRecon";

const PERIODS = [
  { key: "7-day", label: "7 days", days: 7 },
  { key: "15-day", label: "15 days", days: 15 },
  { key: "month", label: "Full month", days: null },
];

const money = (n) =>
  "₹" +
  Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const addDaysInput = (isoDate, days) => {
  const d = new Date(isoDate);
  d.setDate(d.getDate() + days - 1);
  return fmtDateInput(d);
};
const monthEndInput = (isoDate) => {
  const d = new Date(isoDate);
  return fmtDateInput(new Date(d.getFullYear(), d.getMonth() + 1, 0));
};

const Kpi = ({ label, value, sub, tone }) => (
  <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-center">
    <div className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
      {label}
    </div>
    <div className={`mt-1 text-xl font-semibold tabular-nums ${tone || "text-gray-900"}`}>
      {value}
    </div>
    {sub != null && <div className="text-[11px] text-gray-400">{sub}</div>}
  </div>
);

const TYPE_CHIP = {
  receipt: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  payment: "bg-rose-50 text-rose-700 border border-rose-200",
};

const EntryTable = ({ rows, withRemarks, amountLabel }) => {
  const withCust = rows.some((r) => r.userId || r.name || r.plan);
  const withOurAmt = rows.some((r) => r.ourAmount != null);
  const withType = rows.some((r) => r.entryType);
  const cols =
    1 + (withType ? 1 : 0) + 1 + (withOurAmt ? 1 : 0) + (withCust ? 3 : 0) + (withRemarks ? 2 : 0);
  return (
    <div className="max-h-96 overflow-y-auto">
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 bg-gray-50">
          <tr className="text-[11px] uppercase tracking-wider text-gray-500">
            <th className="px-4 py-2 text-left">Date</th>
            {withType && <th className="px-4 py-2 text-left">Type</th>}
            <th className="px-4 py-2 text-right">
              {withOurAmt ? "Bank amt" : amountLabel || "Amount"}
            </th>
            {withOurAmt && <th className="px-4 py-2 text-right">Ledger amt</th>}
            {withCust && <th className="px-4 py-2 text-left">User Id</th>}
            {withCust && <th className="px-4 py-2 text-left">Name</th>}
            {withCust && <th className="px-4 py-2 text-left">Plan</th>}
            {withRemarks && <th className="px-4 py-2 text-left">UPI</th>}
            {withRemarks && <th className="px-4 py-2 text-left">Remarks</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={cols} className="px-4 py-8 text-center text-gray-400">
                None
              </td>
            </tr>
          ) : (
            rows.map((r, i) => (
              <tr key={i} className="hover:bg-cyan-50/30">
                <td className="px-4 py-2 text-gray-600">{fmtDate(r.date)}</td>
                {withType && (
                  <td className="px-4 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${TYPE_CHIP[r.entryType] || TYPE_CHIP.receipt}`}>
                      {r.entryType === "payment" ? "Payment" : "Receipt"}
                    </span>
                  </td>
                )}
                <td className="px-4 py-2 text-right tabular-nums font-medium">
                  {money(r.amount)}
                </td>
                {withOurAmt && (
                  <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                    {r.ourAmount != null ? money(r.ourAmount) : "—"}
                  </td>
                )}
                {withCust && (
                  <td className="px-4 py-2 text-xs text-gray-600">
                    {r.userId || "—"}
                  </td>
                )}
                {withCust && (
                  <td className="px-4 py-2 text-xs text-gray-700">
                    {r.name || "—"}
                  </td>
                )}
                {withCust && (
                  <td className="px-4 py-2 text-[11px] text-gray-400">
                    {r.plan || "—"}
                  </td>
                )}
                {withRemarks && (
                  <td className="px-4 py-2 text-xs text-gray-500">
                    {r.isUpi ? r.upiRef || "UPI" : ""}
                  </td>
                )}
                {withRemarks && (
                  <td className="px-4 py-2 text-xs text-gray-500">{r.remarks}</td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

const NcsBankRecon = () => {
  const dispatch = useDispatch();
  const [partnerCode, setPartnerCode] = usePartnerCode();

  const [periodType, setPeriodType] = useState("7-day");
  const [fromDate, setFromDate] = useState(fmtDateInput());
  const [toDate, setToDate] = useState(addDaysInput(fmtDateInput(), 7));
  const [bankFile, setBankFile] = useState(null);
  const [ourFile, setOurFile] = useState(null);
  const [amountTolerance, setAmountTolerance] = useState("");
  const [saving, setSaving] = useState(false);
  const [runId, setRunId] = useState(null);
  const [tab, setTab] = useState("matched");

  useEffect(() => {
    if (periodType === "month") setToDate(monthEndInput(fromDate));
    else {
      const days = PERIODS.find((p) => p.key === periodType)?.days || 7;
      setToDate(addDaysInput(fromDate, days));
    }
  }, [periodType, fromDate]);

  const { data: runsData } = useGetNbrRunsQuery({ partnerCode });
  const runs = runsData?.runs || [];

  const effectiveRunId = runId || runs[0]?._id || null;
  const { data: runData, isFetching: runLoading } = useGetNbrRunQuery(
    { partnerCode, id: effectiveRunId },
    { skip: !effectiveRunId },
  );
  const run = runData?.run;

  const isNcs = run?.ourLayout === "new-connection";
  const ncsDeposits = isNcs && !!run?.ourHasDepositColumn;

  const tabRows = useMemo(() => {
    if (!runData) return [];
    if (tab === "matched") return runData.matched || [];
    if (tab === "bankOnly") return runData.bankOnly || [];
    return runData.ourOnly || [];
  }, [runData, tab]);

  const submit = async (e) => {
    e.preventDefault();
    if (!bankFile || !ourFile) {
      Swal.fire({ icon: "warning", title: "Upload both files — bank statement and our sheet." });
      return;
    }
    try {
      setSaving(true);
      const res = await createReconRun({
        partnerCode,
        periodType,
        fromDate,
        toDate,
        bankFile,
        ourFile,
        amountTolerance: amountTolerance === "" ? undefined : Number(amountTolerance),
      });
      dispatch(appApi.util.invalidateTags(["NetplusBankRecon"]));
      setRunId(res.data?.run?._id || null);
      setBankFile(null);
      setOurFile(null);
      Swal.fire({
        icon: "success",
        title: res.data?.message || "Reconciled",
        timer: 2200,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Reconciliation failed",
        html:
          err?.response?.data?.run?.validation?.errors
            ?.map((x) => `<div>${x}</div>`)
            .join("") ||
          err?.response?.data?.message ||
          "Something went wrong.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Bank Reconciliation" motherMenu="Netplus" />

      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        {/* upload */}
        <section className={`${panelClass} p-6`}>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">New reconciliation</h2>
            <PartnerPicker value={partnerCode} onChange={setPartnerCode} />
          </div>
          <form onSubmit={submit} className="mt-4 space-y-4">
            <div>
              <span className={labelClass}>Period</span>
              <div className="inline-flex rounded-xl border border-gray-300 p-0.5">
                {PERIODS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setPeriodType(p.key)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                      periodType === p.key
                        ? "bg-cyan-600 text-white"
                        : "text-gray-500 hover:text-gray-800"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className={labelClass}>From</span>
                <input
                  type="date"
                  className={fieldClass}
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div>
                <span className={labelClass}>To</span>
                <input
                  type="date"
                  className={fieldClass}
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
            </div>
            <div>
              <span className={labelClass}>Bank statement (.xlsx)</span>
              <input
                type="file"
                accept=".xlsx,.xls"
                className={`${fieldClass} file:mr-3 file:rounded-lg file:border-0 file:bg-cyan-50 file:px-3 file:py-1.5 file:text-cyan-700`}
                onChange={(e) => setBankFile(e.target.files?.[0] || null)}
              />
              <p className="mt-1 text-[11px] text-gray-400">
                Needs a <b>Date</b> column and at least one of: <b>Payment</b> (withdrawal/debit) or <b>Receipt</b> (deposit/credit).
              </p>
            </div>
            <div>
              <span className={labelClass}>
                Our ledger (.xlsx)
              </span>
              <input
                type="file"
                accept=".xlsx,.xls"
                className={`${fieldClass} file:mr-3 file:rounded-lg file:border-0 file:bg-cyan-50 file:px-3 file:py-1.5 file:text-cyan-700`}
                onChange={(e) => setOurFile(e.target.files?.[0] || null)}
              />
              <p className="mt-1 text-[11px] text-gray-400">
                Date + Payment and/or Receipt columns. A <b>new-connections list</b> (User Id / Plan / Activation Date) also works — amount taken from the plan.
              </p>
            </div>
            <div>
              <span className={labelClass}>Amount tolerance (₹)</span>
              <input
                type="number"
                min="0"
                step="1"
                placeholder="0 = exact  ·  10 recommended for a plan-based sheet"
                className={fieldClass}
                value={amountTolerance}
                onChange={(e) => setAmountTolerance(e.target.value)}
              />
            </div>
            <button type="submit" className={btnPrimary} disabled={saving}>
              {saving ? "Reconciling…" : "Reconcile"}
            </button>
            <p className="text-[11px] text-gray-400">
              Matches bank entries (payments &amp; receipts) against our ledger.
              Re-uploading an overlapping period for {partnerCode} replaces the earlier run.
            </p>
          </form>

          <div className="mt-6">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
              Past runs
            </div>
            <div className="space-y-1">
              {runs.length === 0 && (
                <div className="text-xs text-gray-400">None yet.</div>
              )}
              {runs.map((r) => (
                <button
                  key={r._id}
                  onClick={() => setRunId(r._id)}
                  className={`flex w-full items-center justify-between rounded-lg border px-3 py-1.5 text-xs ${
                    effectiveRunId === r._id
                      ? "border-cyan-400 bg-cyan-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <span>
                    {fmtDate(r.fromDate)} – {fmtDate(r.toDate)}
                  </span>
                  <span className="flex items-center gap-1">
                    {!r.isCurrent && (
                      <span className="text-[10px] uppercase text-gray-400">old</span>
                    )}
                    <Badge
                      tone={
                        r.state === "reconciled"
                          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : "bg-rose-50 text-rose-700 border-rose-200"
                      }
                    >
                      {r.counts?.bankOnly + r.counts?.ourOnly === 0
                        ? "clean"
                        : `${(r.counts?.bankOnly || 0) + (r.counts?.ourOnly || 0)} diff`}
                    </Badge>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* result */}
        <section className={panelClass}>
          {!run ? (
            <div className="p-10 text-center text-gray-400">
              {runLoading ? "Loading…" : "Upload both sheets to see the reconciliation."}
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-5 py-3">
                <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  {fmtDate(run.fromDate)} – {fmtDate(run.toDate)} · {run.periodType}
                </h2>
                <button
                  className={btnGhost}
                  onClick={() => downloadReconExcel(run._id)}
                >
                  Download Excel
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 p-5 sm:grid-cols-3 lg:grid-cols-5">
                <Kpi
                  label="Bank entries"
                  value={money(run.totals?.bankTotal)}
                  sub={`${run.bankRowsParsed ?? 0} rows`}
                />
                {isNcs ? (
                  <>
                    <Kpi
                      label="Matched"
                      value={run.counts?.matched ?? 0}
                      sub={money(run.totals?.matchedTotal)}
                      tone="text-emerald-600"
                    />
                    <Kpi
                      label="In bank, not in ledger"
                      value={run.counts?.bankOnly ?? 0}
                      sub={money(run.totals?.bankOnlyTotal)}
                      tone="text-rose-600"
                    />
                    <Kpi
                      label="Connections, no bank entry"
                      value={run.counts?.ourOnly ?? 0}
                      sub={`${run.ourRowsParsed ?? 0} connections`}
                      tone="text-amber-600"
                    />
                    <Kpi
                      label={ncsDeposits ? "Our receipts total" : "Plan value (expected)"}
                      value={money(run.totals?.ourTotal)}
                      sub={ncsDeposits ? "from Receipts column" : "from plan prices"}
                    />
                  </>
                ) : (
                  <>
                    <Kpi label="Ledger total" value={money(run.totals?.ourTotal)} />
                    <Kpi
                      label="Difference"
                      value={money((run.totals?.bankTotal || 0) - (run.totals?.ourTotal || 0))}
                      tone={
                        Math.abs((run.totals?.bankTotal || 0) - (run.totals?.ourTotal || 0)) < 0.01
                          ? "text-emerald-600"
                          : "text-rose-600"
                      }
                    />
                    <Kpi
                      label="Matched"
                      value={run.counts?.matched ?? 0}
                      sub={money(run.totals?.matchedTotal)}
                      tone="text-emerald-600"
                    />
                    <Kpi
                      label="Unmatched"
                      value={(run.counts?.bankOnly || 0) + (run.counts?.ourOnly || 0)}
                      sub={`${run.counts?.bankOnly || 0} bank · ${run.counts?.ourOnly || 0} ledger`}
                      tone="text-rose-600"
                    />
                  </>
                )}
              </div>

              {isNcs && (
                <div className="mx-5 mb-1 rounded-xl bg-amber-50 px-4 py-2 text-[11px] leading-relaxed text-amber-700">
                  {ncsDeposits ? (
                    <>
                      Matched on the <b>Receipts</b> column of your connections sheet
                      against the bank's receipts, {run.amountTolerance
                        ? `within ₹${run.amountTolerance}`
                        : "exact amount"}
                      . "Connections, no bank entry" lists connections whose receipt has no
                      matching bank line.
                    </>
                  ) : (
                    <>
                      Your sheet is a <b>new-connections list</b> with no receipt amount column,
                      so the expected amount is taken from each plan price and matched to
                      a bank receipt within ₹{run.amountTolerance ?? 0}. "Connections, no
                      bank entry" lists new connections with no matching bank receipt — the
                      figure shown is the plan price, not a recorded receipt.
                    </>
                  )}
                </div>
              )}

              <div className="flex gap-2 border-b border-gray-100 px-5 pb-3">
                {[
                  ["matched", `Matched (${run.counts?.matched ?? 0})`],
                  ["bankOnly", `In bank, not in ledger (${run.counts?.bankOnly ?? 0})`],
                  [
                    "ourOnly",
                    isNcs
                      ? `Connections, no bank entry (${run.counts?.ourOnly ?? 0})`
                      : `In ledger, not in bank (${run.counts?.ourOnly ?? 0})`,
                  ],
                ].map(([k, label]) => (
                  <button
                    key={k}
                    onClick={() => setTab(k)}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                      tab === k
                        ? "bg-cyan-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <EntryTable
                rows={tabRows}
                withRemarks={tab !== "ourOnly"}
                amountLabel={
                  tab === "ourOnly" && isNcs && !ncsDeposits ? "Plan price" : undefined
                }
              />

              {(run.validation?.warnings || []).length > 0 && (
                <div className="border-t border-gray-100 px-5 py-3 text-xs text-amber-700">
                  {run.validation.warnings.map((w, i) => (
                    <div key={i}>⚠ {w}</div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </>
  );
};

export default NcsBankRecon;
